/* eslint-disable jsx-a11y/no-redundant-roles -- Explicit list roles preserve Safari semantics when list markers are hidden. */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Dialog, DialogPanel, DialogTitle, Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import { Bars3Icon, ChevronDownIcon, CheckIcon, ClipboardIcon, DocumentArrowUpIcon, QuestionMarkCircleIcon, Cog6ToothIcon, XMarkIcon, ArrowUturnLeftIcon, MinusIcon, PlusIcon, DocumentDuplicateIcon } from '@heroicons/react/16/solid';
import { Analytics } from '@vercel/analytics/react';
import { tools, ToolName, transformText } from './transforms';
import './index.css';

function readSetting(key: string, fallback: string): string {
  try { return localStorage.getItem(key) ?? fallback; } catch { return fallback; }
}
function useSetting(key: string, fallback: string) {
  const [value, setValue] = useState(() => readSetting(key, fallback));
  useEffect(() => { try { localStorage.setItem(key, value); } catch { /* Preferences are optional. */ } }, [key, value]);
  return [value, setValue] as const;
}
function boundedNumber(value: string, fallback: number, min: number, max: number) {
  const number = Number(value);
  return Number.isFinite(number) ? Math.min(max, Math.max(min, number)) : fallback;
}

type Panel = 'tools' | 'settings' | 'help' | null;
export default function AlephTools() {
  const [input, setInput] = useState('');
  const [toolName, setToolName] = useState<ToolName>('Hebrew Keyboard');
  const [keep, setKeep] = useSetting('keepPunctuation', 'false');
  const [finals, setFinals] = useSetting('convertFinalLetters', 'true');
  const [clean, setClean] = useSetting('cleanPunctuation', 'false');
  const [fontSetting, setFontSetting] = useSetting('fontSize', '18');
  const [splitSetting, setSplitSetting] = useSetting('splitPercent', '50');
  const fontSize = boundedNumber(fontSetting, 18, 16, 32);
  const split = boundedNumber(splitSetting, 50, 25, 75);
  const [panel, setPanel] = useState<Panel>(null);
  const [message, setMessage] = useState('');
  const [error, setError] = useState(false);
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const [previousInput, setPreviousInput] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const editor = useRef<HTMLTextAreaElement>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const workspace = useRef<HTMLElement>(null);
  const selection = useRef({ start: 0, end: 0 });
  const tool = tools.find(item => item.title === toolName)!;
  const output = useMemo(() => transformText(input, toolName, {
    keepPunctuation: keep === 'true', finalLetters: finals === 'true', cleanPunctuation: clean === 'true',
  }), [input, toolName, keep, finals, clean]);
  const keyboardTool = toolName === 'Hebrew Keyboard' || toolName === 'English Keyboard';

  function announce(text: string, isError = false) {
    setMessage(text); setError(isError);
  }
  useEffect(() => {
    if (!message) return;
    const timeout = window.setTimeout(() => setMessage(''), error ? 10000 : 5000);
    return () => window.clearTimeout(timeout);
  }, [message, error]);
  useEffect(() => {
    setCopied(false);
  }, [output]);
  useEffect(() => {
    if (!copied) return;
    const timeout = window.setTimeout(() => setCopied(false), 2000);
    return () => window.clearTimeout(timeout);
  }, [copied]);

  function selectTool(name: ToolName) {
    setToolName(name); setPanel(null); announce(`${name} selected.`);
  }
  useEffect(() => {
    function shortcut(event: KeyboardEvent) {
      // Do not replace browser tab shortcuts. Use an additional Alt modifier.
      if (panel || !(event.metaKey || event.ctrlKey) || !event.altKey || event.shiftKey) return;
      const index = Number(event.key) - 1;
      if (Number.isInteger(index) && index >= 0 && index < tools.length) {
        event.preventDefault(); setToolName(tools[index].title);
        announce(`${tools[index].title} selected.`);
      } else if (event.key.toLowerCase() === 'p' && keyboardTool) {
        event.preventDefault(); setKeep(value => value === 'true' ? 'false' : 'true');
      }
    }
    window.addEventListener('keydown', shortcut);
    return () => window.removeEventListener('keydown', shortcut);
  }, [panel, keyboardTool, setKeep]);

  function focusEditor(position?: number) {
    requestAnimationFrame(() => {
      editor.current?.focus();
      if (position !== undefined) editor.current?.setSelectionRange(position, position);
    });
  }
  function rememberSelection() {
    if (editor.current) selection.current = { start: editor.current.selectionStart, end: editor.current.selectionEnd };
  }
  async function copy() {
    if (!output || busy) return;
    setBusy(true);
    try {
      await navigator.clipboard.writeText(output);
      setCopied(true); announce('Copied to clipboard.');
    } catch {
      announce('Copy was blocked. Select the result and use your keyboard to copy.', true);
    } finally { setBusy(false); }
  }
  async function paste() {
    if (busy) return;
    const { start, end } = selection.current;
    setBusy(true);
    try {
      const text = await navigator.clipboard.readText();
      setInput(value => value.slice(0, start) + text + value.slice(end));
      setPreviousInput(null);
      focusEditor(start + text.length); announce('Text pasted.');
    } catch {
      focusEditor(); announce('Paste was blocked. Use your keyboard to paste into Input.', true);
    } finally { setBusy(false); }
  }
  function clearInput() {
    setPreviousInput(input); setInput(''); selection.current = { start: 0, end: 0 };
    focusEditor(0); announce('Input cleared. Use Undo to restore it.');
  }
  function undoClear() {
    if (previousInput === null) return;
    const restored = previousInput;
    setInput(restored); setPreviousInput(null); focusEditor(restored.length); announce('Input restored.');
  }
  async function importFiles(files: FileList | null) {
    if (!files?.length) return;
    if (files.length !== 1) { announce('Choose one text file at a time.', true); return; }
    const file = files[0];
    if (!/\.(txt|md|text)$/i.test(file.name)) {
      announce('Choose a .txt, .md, or .text file. Rich text is not supported.', true); return;
    }
    if (file.size > 5 * 1024 * 1024) { announce('Choose a text file smaller than 5 MB.', true); return; }
    try {
      const text = await file.text();
      setPreviousInput(input || null); setInput(text); selection.current = { start: text.length, end: text.length };
      focusEditor(text.length); announce('Text file opened.');
    } catch { announce('This file could not be read. Try another text file.', true); }
  }
  function resize(clientX: number) {
    if (!workspace.current) return;
    const rect = workspace.current.getBoundingClientRect();
    const percent = (clientX - rect.left) / rect.width * 100;
    setSplitSetting(String(Math.min(75, Math.max(25, percent))));
  }
  function toolOptions() {
    return <div className="tool-options">
      {keyboardTool && <label className="option-label" htmlFor="keep-punctuation">
        <input id="keep-punctuation" name="keepPunctuation" type="checkbox" checked={keep === 'true'} onChange={event => setKeep(String(event.target.checked))} />
        <span>Keep punctuation</span>
      </label>}
      {toolName === 'Square Hebrew' && <>
        <label className="option-label" htmlFor="final-letters"><input id="final-letters" name="finalLetters" type="checkbox" checked={finals === 'true'} onChange={event => setFinals(String(event.target.checked))} /><span>Final letters</span></label>
        <label className="option-label" htmlFor="clean-punctuation"><input id="clean-punctuation" name="cleanPunctuation" type="checkbox" checked={clean === 'true'} onChange={event => setClean(String(event.target.checked))} /><span>Clean punctuation</span></label>
      </>}
    </div>;
  }

  return <div className="app-shell isolate min-h-dvh antialiased" style={{ '--editor-size': `${fontSize / 16}rem`, '--split': `${split}%` } as React.CSSProperties}>
    <a className="skip-link" href="#input-text">Skip to input</a>
    <header className="app-header">
      <div className="header-row">
        <h1 className="brand text-balance"><a href="/" aria-label="Aleph Tools homepage">Aleph<span className="brand-light"> Tools</span></a></h1>
        <div className="desktop-tools hidden lg:flex">
          <Menu>
            <MenuButton className="button tool-trigger" aria-label={`Choose tool: ${toolName}`}><span>{toolName}</span><ChevronDownIcon aria-hidden="true" /></MenuButton>
            <MenuItems anchor="bottom start" className="tool-menu surface-popover" modal={false}>
              <p className="menu-heading">Text tools</p>
              {tools.map(item => <MenuItem key={item.title}>
                <button type="button" className="tool-menu-item" aria-current={toolName === item.title ? 'true' : undefined} onClick={() => selectTool(item.title)}>
                  <div className="tool-check">{item.title === toolName && <CheckIcon aria-hidden="true" />}</div>
                  <div className="min-w-0"><p className="tool-title">{item.title}</p><p className="tool-description">{item.description}</p></div>
                </button>
              </MenuItem>)}
            </MenuItems>
          </Menu>
        </div>
      <div className="mode-row">
        <button type="button" className="mobile-tool-summary lg:hidden" onClick={() => setPanel('tools')} aria-haspopup="dialog"><span>{toolName}</span><ChevronDownIcon aria-hidden="true" /></button>
        
        {toolOptions()}
      </div>
        <nav className="header-actions" aria-label="App actions">
          <button type="button" className="button icon-button mobile-nav-button lg:hidden" onClick={() => setPanel('tools')} aria-label="Choose tool" aria-haspopup="dialog"><Bars3Icon aria-hidden="true" /></button>
          <button type="button" className="button icon-button" onClick={() => setPanel('help')} aria-label="Help" aria-haspopup="dialog"><QuestionMarkCircleIcon aria-hidden="true" /><span className="hidden lg:inline">Help</span></button>
          <button type="button" className="button icon-button" onClick={() => setPanel('settings')} aria-label="Settings" aria-haspopup="dialog"><Cog6ToothIcon aria-hidden="true" /><span className="hidden lg:inline">Settings</span></button>
          <a href="https://github.com/d7mtg/AlephTools" className="button native-link hidden lg:inline-flex" target="_blank" rel="noreferrer">Native app</a>
        </nav>
      </div>

    </header>

    <main ref={workspace} className="workspace" aria-label="Text conversion">
      <section className={`editor-panel input-panel ${dragOver ? 'is-dragging' : ''}`} aria-labelledby="input-label"
        onDragOver={event => { event.preventDefault(); setDragOver(true); }}
        onDragLeave={event => { if (!event.currentTarget.contains(event.relatedTarget as Node)) setDragOver(false); }}
        onDrop={event => { event.preventDefault(); setDragOver(false); void importFiles(event.dataTransfer.files); }}>
        <div className="panel-toolbar">
          <div className="panel-heading"><label id="input-label" htmlFor="input-text">Input</label><p id="input-hint" className="sr-only">{tool.input}</p></div>
          <div className="panel-actions">
            {previousInput !== null && <button type="button" className="button icon-button undo-button" aria-label="Undo" title="Undo" onClick={undoClear}><ArrowUturnLeftIcon aria-hidden="true" /></button>}
            <button type="button" className="button icon-button" aria-label="Open a file" title="Open a file" onClick={() => fileInput.current?.click()}><DocumentArrowUpIcon aria-hidden="true" /></button>
            <button type="button" className="button" aria-label="Paste" title="Paste" onClick={paste} disabled={busy}><ClipboardIcon aria-hidden="true" /><span>Paste</span></button>
            <button type="button" className="button" aria-label="Clear" title="Clear" onClick={clearInput} disabled={!input}><XMarkIcon aria-hidden="true" /><span>Clear</span></button>
          </div>
        </div>
        <textarea ref={editor} id="input-text" name="inputText" aria-describedby="input-hint" className="text-editor" dir={tool.inputDir}
          value={input} placeholder="Type or paste text here..." spellCheck={false} autoCapitalize="off" autoCorrect="off"
          onChange={event => { setInput(event.target.value); setPreviousInput(null); }}
          onSelect={rememberSelection} onBlur={rememberSelection}
          onKeyDown={event => { if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'z' && previousInput !== null && !input && !event.shiftKey) { event.preventDefault(); undoClear(); } }} />
        <input className="sr-only" ref={fileInput} name="textFile" type="file" accept=".txt,.md,.text,text/plain,text/markdown" aria-label="Open text file" tabIndex={-1}
          onChange={event => { void importFiles(event.target.files); event.target.value = ''; }} />
        {dragOver && <div className="drop-label" aria-hidden="true"><DocumentArrowUpIcon />Drop one text file</div>}
      </section>

      <div role="separator" aria-label="Resize input and output panels" aria-orientation="vertical" aria-valuemin={25} aria-valuemax={75} aria-valuenow={Math.round(split)} aria-valuetext={`${Math.round(split)} percent input width`} tabIndex={0} className="panel-divider"
        onDoubleClick={() => setSplitSetting('50')}
        onPointerDown={event => { event.preventDefault(); event.currentTarget.setPointerCapture(event.pointerId); resize(event.clientX); }}
        onPointerMove={event => { if (event.currentTarget.hasPointerCapture(event.pointerId)) resize(event.clientX); }}
        onPointerUp={event => { if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId); }}
        onKeyDown={event => {
          if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          const value = event.key === 'Home' ? 25 : event.key === 'End' ? 75 : split + (event.key === 'ArrowLeft' ? -5 : 5);
          setSplitSetting(String(Math.min(75, Math.max(25, value))));
        }}><span /></div>

      <section className="editor-panel output-panel" aria-labelledby="output-label">
        <div className="panel-toolbar">
          <div className="panel-heading"><h2 id="output-label">Output</h2><p id="output-hint" className="sr-only">{tool.output}</p></div>
          <button type="button" className="button primary-button" onClick={copy} disabled={!output || busy} aria-label={copied ? 'Copied' : 'Copy output'}>
            <span className="copy-icons" aria-hidden="true"><DocumentDuplicateIcon className={copied ? 'icon-hidden' : 'icon-visible'} /><CheckIcon className={copied ? 'icon-visible' : 'icon-hidden'} /></span><span>{copied ? 'Copied' : 'Copy'}</span>
          </button>
        </div>
        {output ? <div className={`text-editor result ${toolName === 'Gematria' ? 'tabular-nums' : ''}`} dir={tool.outputDir} lang={['Hebrew Keyboard', 'Remove Niqqud', 'Square Hebrew'].includes(toolName) ? 'he' : undefined}
          role="region" aria-label="Converted text" aria-describedby="output-hint" tabIndex={0}>{output}</div>
          : <div className="text-editor empty-result">Result appears here.</div>}
      </section>
    </main>

    <div className={`status-message ${message ? 'status-visible' : ''} ${error ? 'status-error' : ''}`} role="status" aria-live="polite" aria-atomic="true">{message}</div>

    <Dialog open={panel !== null} onClose={() => setPanel(null)} className="app-dialog">
      <div className="dialog-backdrop" aria-hidden="true" />
      <div className="dialog-position"><DialogPanel className="dialog-panel surface-popover">
        <div className="dialog-heading"><DialogTitle className="text-balance">{panel === 'tools' ? 'Choose a tool' : panel === 'settings' ? 'Settings' : 'Help'}</DialogTitle><button type="button" className="button icon-button" aria-label="Close dialog" onClick={() => setPanel(null)}><XMarkIcon aria-hidden="true" /></button></div>
        <div className="dialog-body">
          {panel === 'tools' && <nav aria-label="Text tools"><ul role="list" className="mobile-tool-list">{tools.map(item => <li key={item.title}><button type="button" className="tool-menu-item" onClick={() => selectTool(item.title)} aria-current={toolName === item.title ? 'true' : undefined}><div className="min-w-0"><p className="tool-title">{item.title}</p><p className="tool-description">{item.description}</p></div><div className="tool-check">{item.title === toolName && <CheckIcon aria-hidden="true" />}</div></button></li>)}</ul><a className="button dialog-native-link" href="https://github.com/d7mtg/AlephTools" target="_blank" rel="noreferrer">Get the native app</a></nav>}
          {panel === 'settings' && <div className="settings-content">
            <section aria-labelledby="text-size-label"><h3 id="text-size-label">Text size</h3><p>Make input and output easier to read.</p><div className="size-controls"><button type="button" className="button icon-button" aria-label="Decrease text size" disabled={fontSize <= 16} onClick={() => setFontSetting(String(fontSize - 1))}><MinusIcon aria-hidden="true" /></button><output aria-live="polite" className="tabular-nums" aria-label="Text size">{fontSize} px</output><button type="button" className="button icon-button" aria-label="Increase text size" disabled={fontSize >= 32} onClick={() => setFontSetting(String(fontSize + 1))}><PlusIcon aria-hidden="true" /></button></div><p className="size-preview" dir="rtl" lang="he" style={{ '--preview-size': `${fontSize / 16}rem` } as React.CSSProperties}>שָׁלוֹם, עוֹלָם</p></section>
            <section><h3>Panel widths</h3><p>On a wide screen, drag the divider or use its arrow keys.</p><button type="button" className="button" onClick={() => { setSplitSetting('50'); announce('Panel widths reset.'); }}>Reset panel widths</button></section>
            <section><h3>Appearance</h3><p>Light and dark mode follow your device settings.</p></section>
          </div>}
          {panel === 'help' && <div className="help-content">
            <p>Choose a tool, enter text, then copy the result.</p>
            <section><h3>Current tool: {tool.title}</h3><p>{tool.description}</p><p className="example-text" dir="auto">{tool.sample}</p><button type="button" className="button" onClick={() => { setInput(tool.example); setPreviousInput(input || null); setPanel(null); focusEditor(tool.example.length); }}>Try an example</button>{keyboardTool && <p>Keep punctuation preserves quotation marks, apostrophes, commas, and periods.</p>}{toolName === 'Square Hebrew' && <><p>Final letters adds ך, ם, ן, ף, and ץ at word endings.</p><p>Clean punctuation removes inscription brackets, line numbers, and damage marks. It replaces dots with spaces.</p></>} {toolName === 'Gematria' && <p>Uses standard letter values. Final letters have the same values as their regular forms. Other characters are ignored.</p>}</section>
            <section><h3>Files and recovery</h3><p>Open or drop one .txt, .md, or .text file, up to 5 MB. Files are read on your device. Rich text is not supported.</p><p>After Clear or a file import, use Undo to restore the previous input. After Clear, your keyboard undo shortcut also works.</p></section>
            <section><h3>Keyboard shortcuts</h3><p>Use Command + Option + 1 through 7 on Mac, or Control + Alt + 1 through 7 on Windows, to choose tools in this order.</p><ol role="list" className="shortcut-list">{tools.map((item, index) => <li key={item.title}><span className="tabular-nums">{index + 1}</span>{item.title}</li>)}</ol><p>Use the same modifiers with P to toggle Keep punctuation in a keyboard tool.</p></section>
            <p className="credit">Made by <a href="https://d7mtg.com/" target="_blank" rel="noreferrer">Yitzchok Sabel</a>.</p>
          </div>}
        </div>
      </DialogPanel></div>
    </Dialog>
    <Analytics />
  </div>;
}
